import React, { useEffect, useState, useMemo } from 'react';
import { Button, IconButton, Pagination, Table, Tooltip, Whisper } from 'rsuite';
import VisibleIcon from '@rsuite/icons/Visible';
import axios from 'axios';
import globalIP from '@/GlobelIP';
import { useNavigate } from 'react-router-dom';
import RevisionViewModal from './RevisionViewModal';
import ugr from '@/UGRoute';

const { Column, HeaderCell, Cell } = Table;

const CostingViewTable = ({ costingFor }) => {
  const [tableAllData, setTableAllData] = useState([]);
  const [selectedData, setSelectedData] = useState();
  const navigate = useNavigate();
  const [revisionModal, setRevisionModal] = useState(false);
  const [allDataLength, setAllDataLength] = useState();
  const [currentPage, setcCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const columns = useMemo(
    () =>
      [
        {
          key: 'Image',
          label: 'Image',
          renderCell: ({ product }) => {
            const renderTooltip = imageSrc => (
              <Tooltip style={{ backgroundColor: 'white' }} className="prdt-img">
                <img src={imageSrc} alt="Product Image" style={{ width: 400, height: 'auto' }} />
              </Tooltip>
            );

            return product && (
              <Whisper
                trigger="hover"
                placement="right"
                speaker={renderTooltip(
                  `${globalIP}${product?.attributes?.CoverImage?.data?.attributes?.url}`
                )}
              >
                <img
                  src={`${globalIP}${product?.attributes?.CoverImage?.data?.attributes?.url}`}
                  alt="Product Image"
                  style={{ width: 50, height: 50, marginTop: '-16px' }}
                />
              </Whisper>
            );
          }
        },
        {
          label: 'AliasCode',
          key: 'AliasCode',
          fixed: false,
          renderCell: ({ product }) => {
            return product?.attributes?.ProductId;
          }
        },
        {
          label: 'Description',
          key: 'Description',
          fixed: false,
          renderCell: ({ product }) => {
            return product?.attributes?.Description;
          }
        },
        {
          label: 'Product Name',
          key: 'ProductName',
          fixed: false,
          renderCell: ({ product }) => {
            return product?.attributes?.ProductName;
          }
        },
        {
          label: 'Dimension',
          key: 'Dimension',
          fixed: false,
          renderCell: ({ product }) => {
            return `${product?.attributes.Length} x ${product?.attributes.Width} x ${product?.attributes.Height} ${product?.attributes.SizeUnit}`;
          }
        },
        {
          label: 'Category Name',
          key: 'CategoryName',
          fixed: false,
          renderCell: ({ product }) => {
            return product?.attributes?.CategoryName;
          }
        },
        {
          label: 'FinishName',
          key: 'FinishName',
          fixed: false,
          renderCell: ({ product }) => {
            return product?.attributes?.FinishName;
          }
        },
        {
          label: 'ProductType Name',
          key: 'ProductTypeName',
          fixed: false,
          renderCell: ({ product }) => {
            return product?.attributes?.ProductTypeName;
          }
        },
        {
          label: 'CBM',
          key: 'CBM',
          fixed: false,
          renderCell: ({ product }) => {
            return product?.attributes?.cbm;
          }
        },
        costingFor.for == 'buyer' && {
          label: 'Buyer Name',
          key: 'buyerName',
          fixed: false,
          renderCell: ({ attributes }) => {
            return attributes?.buyername;
          }
        },

        {
          label: 'Action',
          key: 'action',
          fixed: false,
          renderCell: ({ product, attributes, id }) => {
            return costingFor.for == 'factory' ? (
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <IconButton
                  icon={<VisibleIcon />}
                  size="sm"
                  onClick={() => {
                    setRevisionModal(true);
                    setSelectedData({ product, attributes, id });
                  }}
                />
                <Button
                  size="sm"
                  onClick={() =>
                    navigate(`/${ugr}/costing`, {
                      state: { ...product, costingFor, isEdit: false }
                    })
                  }
                >
                  View
                </Button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <IconButton
                  icon={<VisibleIcon />}
                  size="sm"
                  onClick={() => {
                    setRevisionModal(true);
                    setSelectedData({ product, attributes, id });
                  }}
                />
                <Button
                  size="sm"
                  onClick={() =>
                    navigate(`/${ugr}/costing`, {
                      state: {
                        ...product,
                        costingFor: { for: 'buyer', name: attributes.buyername },
                        isEdit: false
                      }
                    })
                  }
                >
                  View
                </Button>
              </div>
            );
          }
        }
      ].filter(Boolean),
    [costingFor.for]
  );

  const fetchData = async () => {
    try {
      setIsLoading(true);

      // Skip fetch if costingFor.for is empty
      if (!costingFor.for) {
        setTableAllData([]);
        setAllDataLength(0);
        setIsLoading(false);
        return;
      }

      // If costingFor is buyer, ensure name is selected
      if (costingFor.for === 'buyer' && !costingFor.name) {
        setTableAllData([]);
        setAllDataLength(0);
        setIsLoading(false);
        return;
      }

      // Build query
      let query = `${globalIP}/api/costings?populate=*&filters[costingfor][$eq]=${costingFor.for}`;
      if (costingFor.for === 'buyer') {
        query += `&filters[buyername][$eq]=${costingFor.name}`;
      }

      const factoryAndBuyerData = await axios.get(query);

      const productIds = factoryAndBuyerData.data.data.map(item => item.attributes.ProductId);

      const allProductData = await Promise.all(
        productIds.map(async (id) => {
          try {
            const productData = await axios.get(`${globalIP}/api/products/${id}?populate=*`);
            return productData.data.data;
          } catch (error) {
            console.log('Error fetching product with id:', id, error);
            return null;
          }
        })
      );

      const filteredProductData = allProductData.filter(item => item !== null);

      const mergedData = factoryAndBuyerData.data.data
        .map(item => {
          const product = filteredProductData.find(product => product?.id == item.attributes.ProductId);
          return { ...item, product };
        })
        .filter(item => item.product); // <-- remove items with no product

      setAllDataLength(mergedData.length);

      const paginatedData = [];
      const pageSize = 10;
      for (let i = 0; i < mergedData.length; i += pageSize) {
        paginatedData.push(mergedData.slice(i, i + pageSize));
      }
      setTableAllData(paginatedData);

      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [costingFor.for, costingFor.name]);
  const handleChangeLimit = () => {
    setcCurrentPage(0);
  };
  return (
    <>
      <Table
        bordered
        cellBordered
        autoHeight
        loading={isLoading}
        data={tableAllData[currentPage]?.filter(item => item.product != null)}
        virtualized
      >
        {columns.map(({ fixed, label, key, renderCell }) => {
          return (
            <Column
              fixed={fixed}
              key={key}
              flexGrow={label == 'Image' ? null : 1}
              width={label == 'Image' ? 90 : null}
              align="center"
            >
              <HeaderCell
                style={{
                  fontSize: '11px',
                  fontWeight: 'bold'
                }}
              >
                {label}
              </HeaderCell>
              <Cell dataKey={key}>
                {rowData => (renderCell ? renderCell(rowData) : rowData[key])}
              </Cell>
            </Column>
          );
        })}
      </Table>
      <div style={{ padding: 20 }}>
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          size="xs"
          layout={['-', 'pager', 'skip']}
          total={allDataLength}
          limit={10}
          activePage={currentPage + 1}
          onChangePage={value => setcCurrentPage(value - 1)}
          onChangeLimit={handleChangeLimit}
        />
      </div>
      {revisionModal && (
        <RevisionViewModal
          open={revisionModal}
          close={() => setRevisionModal(false)}
          costingFor={costingFor}
          selectedData={selectedData}
        />
      )}
    </>
  );
};

export default React.memo(CostingViewTable);
